import { FC } from 'react';

import { t } from '@core/i18n';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { Button } from '@shared/components/button-new';
import { AtlasLogoWithErrorIcon } from '@shared/icons/atlas-logo-with-error';
import { ButtonSize, NewButtonColour } from '@shared/types/common/button';

import { styles } from './AtlasPublishError.styles';

export interface AtlasPublishErrorProps extends WithStyles<typeof styles> {
  // New flexible props (preferred)
  bodyText?: string;
  headerText?: string;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  onPrimaryBtnClick?: () => void;
  onSecondaryBtnClick?: () => void;

  // Legacy props (deprecated but maintained for backward compatibility)
  errorReason?: string;
  mode?: 'publish' | 'update';
  onReturnToPreviousScreen?: () => void;
  onTryAgain?: () => void;
}

const AtlasPublishErrorComponent: FC<AtlasPublishErrorProps> = ({
  bodyText,
  classes,
  errorReason,
  headerText,
  mode = 'publish',
  onPrimaryBtnClick,
  onReturnToPreviousScreen,
  onSecondaryBtnClick,
  onTryAgain,
  primaryButtonText,
  secondaryButtonText,
}) => {
  const header =
    headerText ||
    (mode === 'update' ? t('unable_to_update_transaction') : t('unable_to_publish_transaction'));

  const body =
    bodyText ||
    (mode === 'update'
      ? t(
          'we_couldnt_update_this_transaction_on_atlas_because_of_xerror_reasonx_you_can_try_again_or_reach_out_to_xsupport_linkx_if_the_issue_continues',
          {
            error_reason: errorReason || t('an_unexpected_error'),
            support_link: `<a href="mailto:hello@kato.app" class="${classes.supportLink}">support</a>`,
          }
        )
      : t(
          'we_couldnt_publish_this_transaction_to_atlas_because_of_xerror_reasonx_you_can_try_again_or_reach_out_to_xsupport_linkx_if_the_issue_continues',
          {
            error_reason: errorReason || t('an_unexpected_error'),
            support_link: `<a href="mailto:hello@kato.app" class="${classes.supportLink}">support</a>`,
          }
        ));

  const primaryBtnText =
    primaryButtonText || t('return_to_xscreen_namex', { screen_name: t('atlas_tab') });

  const secondaryBtnText = secondaryButtonText || t('try_again');

  const handlePrimaryClick = onPrimaryBtnClick || onReturnToPreviousScreen;
  const handleSecondaryClick = onSecondaryBtnClick || onTryAgain;

  return (
    <div className={classes.root}>
      <div className={classes.contentContainer}>
        <div className={classes.iconSection}>
          <AtlasLogoWithErrorIcon />
        </div>

        <div className={classes.textSection}>
          <h2 className={classes.mainMessage}>{header}</h2>

          <p className={classes.secondaryMessage} dangerouslySetInnerHTML={{ __html: body }} />
        </div>
      </div>

      <div className={classes.buttonRow}>
        {handlePrimaryClick && (
          <Button
            classes={{ root: classes.returnButton }}
            onClick={handlePrimaryClick}
            size={ButtonSize.medium}
            text={primaryBtnText}
            variant="outlined"
          />
        )}

        {handleSecondaryClick && (
          <Button
            classes={{ root: classes.tryAgainButton }}
            colour={NewButtonColour.PrimaryNavy}
            onClick={handleSecondaryClick}
            size={ButtonSize.medium}
            text={secondaryBtnText}
          />
        )}
      </div>
    </div>
  );
};

export const AtlasPublishError = withStyles(styles)(AtlasPublishErrorComponent);
